/**
 * `src/platform/` — the DEPLOYMENT brand tier (WS-A §2.8, D-15′). **This file is the sole copy.**
 *
 * Fetched once at boot from the nginx-served `/branding.json` (Helm ConfigMap), validated
 * against the shared `DeploymentBrandingSchema`, then cached module-locally. A missing,
 * invalid or empty file ⇒ `null` ⇒ the hardwired neutral brand ("Console"). The SHARED piece
 * is the contract, `DeploymentBrandingSchema`; this loader is the console's own.
 *
 * ⛔ The URL is the literal root-absolute `'/branding.json'` and there is deliberately no
 * base-prefixing adapter around it. There used to be one (`prefixedFetch`): the upstream copy
 * hardcoded a root path while this console was served under `/cp-admin/`, so the two
 * disagreed and something had to reconcile them. Both halves of that disagreement are gone —
 * the module is local, and the console serves from the origin root — so the path and the
 * serving base now agree by construction rather than by adapter.
 *
 * ⛔ Never throws. Any failure — network, 404, invalid shape, timeout — resolves to `null`.
 * A console that will not boot because its optional logo is missing is worse than an
 * unbranded console.
 */
import { DeploymentBrandingSchema, type DeploymentBranding } from '@zion/sdk-cp-dp-contracts';

let cached: DeploymentBranding | null = null;
/** The number of the newest `loadDeploymentBranding` call. */
let generation = 0;

/** The cached deployment brand, or null when unset/unfetched. Synchronous. */
export function getDeploymentBranding(): DeploymentBranding | null {
  return cached;
}

/** TEST-ONLY reset of the module cache. */
export function __setDeploymentBrandingForTest(value: DeploymentBranding | null): void {
  cached = value;
}

/** The one deadline over the whole read of `/branding.json`: request, headers and body. */
const BRANDING_DEADLINE_MS = 1500;

/**
 * Fetch + validate `/branding.json` once at boot. Resolves to the validated brand or null
 * (empty `{}`, network error, invalid shape, or timeout). Never throws. As a side effect it sets
 * the module cache, but only when no newer call started; an older call leaves the cache as it is.
 *
 * One `AbortController` bounds the request and the body read together: at 1500 ms it aborts
 * the request and the loader resolves null, so a body that never ends cannot hold the boot. The
 * abort also rejects a deadline that the fetch and `res.json()` race against.
 * The timer clears in `finally` on every path.
 *
 * `fetchImpl` is an injectable seam so the suite can drive every arm without a network; it
 * defaults to the global `fetch`, which is what `main.tsx` uses.
 */
export async function loadDeploymentBranding(
  fetchImpl: typeof fetch = fetch,
): Promise<DeploymentBranding | null> {
  // Each call takes a number; only the newest call writes the cache, so an older load that times
  // out late cannot erase the brand a newer load stored.
  generation += 1;
  const mine = generation;
  const settle = (value: DeploymentBranding | null): DeploymentBranding | null => {
    if (mine === generation) cached = value;
    return value;
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Inside the try: a runtime with no AbortController resolves null and does not stop the boot.
    const controller = new AbortController();
    // Rejects at the deadline, so a fetch or a body that ignores the signal still ends.
    const deadline = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(controller.signal.reason), {
        once: true,
      });
    });
    timer = setTimeout(
      () => controller.abort(new Error('branding.json timeout')),
      BRANDING_DEADLINE_MS,
    );
    const res = await Promise.race([
      fetchImpl('/branding.json', { cache: 'no-store', signal: controller.signal }),
      deadline,
    ]);
    if (!res.ok) {
      // The body of a non-OK answer is never read, so the abort closes it before the timer clears.
      controller.abort(new Error('branding.json not ok'));
      return settle(null);
    }
    const json: unknown = await Promise.race([res.json(), deadline]);
    const parsed = DeploymentBrandingSchema.safeParse(json);
    // Empty `{}` parses to an all-undefined record → treat as neutral (null).
    return settle(parsed.success && Object.keys(parsed.data).length > 0 ? parsed.data : null);
  } catch {
    return settle(null);
  } finally {
    clearTimeout(timer);
  }
}

export type { DeploymentBranding };
